import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AnimalSymbol from "@/components/AnimalSymbol";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

export default function SignupPage() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const referralCode = (params.get("ref") || "").trim().toUpperCase();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await register(email, password, name, referralCode);
      nav("/", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)] p-6">
      <div className="w-full max-w-sm bg-white border border-[var(--app-border)] rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-md bg-[var(--app-bg)] border border-[var(--app-border)] flex items-center justify-center">
            <AnimalSymbol parts={{ ears: "correct", head: "correct", neck: "correct", front: "correct", back: "correct", rear: "correct", tail: "correct" }} size={32} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-lg tracking-tight">Vitality Pedigree</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--app-text-soft)]">Start your free trial</div>
          </div>
        </div>
        <p className="text-xs text-[var(--app-text-soft)] mb-5 mt-2">30 days of full access. No credit card required.</p>

        {referralCode && (
          <div className="mb-5 p-3 rounded border border-emerald-300 bg-emerald-50 text-xs text-emerald-800" data-testid="signup-referral-banner">
            You were referred with code <span className="font-mono-tech font-bold">{referralCode}</span>.
            Your referrer will earn a free month when you subscribe.
          </div>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="name">Name</Label>
            <Input id="name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="signup-email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="signup-password" />
            <div className="text-[10px] text-[var(--app-text-soft)] mt-1">Min 8 characters, with a letter and a number.</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="confirm">Confirm password</Label>
            <Input id="confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="signup-confirm" />
          </div>
          {err && <div className="text-sm text-red-600" data-testid="signup-error">{err}</div>}
          <Button type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="signup-submit">
            {busy ? "Creating account…" : "Start free trial"}
          </Button>
        </form>

        <div className="mt-5 text-center text-sm text-[var(--app-text-soft)]">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 hover:underline" data-testid="link-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
